import React, { useState, useEffect } from 'react';
import { updateGolfer } from '../adminApiService';
import './EditPlayerModal.css';

const EditPlayerModal = ({
  showModal,
  setShowModal,
  player,
  onPlayerUpdated
}) => {
  const [formData, setFormData] = useState({
    name: '',
    worldRanking: '',
    tier: 'TIER_D',
    country: '',
    pgatourId: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Update form data when player prop changes
  useEffect(() => {
    if (player) {
      setFormData({
        name: player.name || '',
        worldRanking: player.worldRanking || '',
        tier: player.tier || 'TIER_D',
        country: player.country || '',
        pgatourId: player.pgatourId || '',
        isActive: player.isActive !== undefined ? player.isActive : true
      });
    }
  }, [player]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Prepare update data - only include fields that have changed
      const updateData = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== player[key]) {
          updateData[key] = formData[key];
        }
      });

      // Convert tier format if needed
      if (updateData.tier && !updateData.tier.startsWith('TIER_')) {
        updateData.tier = `TIER_${updateData.tier}`;
      }

      // Convert world ranking to number
      if (updateData.worldRanking) {
        updateData.worldRanking = parseInt(updateData.worldRanking);
      }

      await updateGolfer(player.id, updateData);
      
      // Call the callback to refresh the player list
      if (onPlayerUpdated) {
        onPlayerUpdated();
      }
      
      setShowModal(false);
    } catch (err) {
      setError(err.message || 'Failed to update player');
      console.error('Error updating player:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setError('');
  };

  if (!showModal || !player) return null;

  return (
    <div className="edit-player-modal-overlay">
      <div className="edit-player-modal">
        <div className="edit-player-modal-header">
          <h2>Edit Player</h2>
          <button className="edit-player-modal-close" onClick={handleClose}>
            ×
          </button>
        </div>

        <form className="player-form" onSubmit={handleSubmit}>
          <div className="edit-player-form-group">
            <label htmlFor="name">Player Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Enter player name"
            />
          </div>

          <div className="form-row">
            <div className="edit-player-form-group">
              <label htmlFor="worldRanking">World Ranking</label>
              <input
                type="number"
                id="worldRanking"
                name="worldRanking"
                value={formData.worldRanking}
                onChange={handleInputChange}
                min="1"
                placeholder="Enter world ranking"
              />
            </div>
            
            <div className="edit-player-form-group">
              <label htmlFor="tier">Tier</label>
              <select
                id="tier"
                name="tier"
                value={formData.tier}
                onChange={handleInputChange}
              >
                <option value="TIER_A">A Tier</option>
                <option value="TIER_B">B Tier</option>
                <option value="TIER_C">C Tier</option>
                <option value="TIER_D">D Tier</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="edit-player-form-group">
              <label htmlFor="country">Country</label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder="Enter country"
              />
            </div>
            
            <div className="edit-player-form-group">
              <label htmlFor="pgatourId">PGA Tour ID</label>
              <input
                type="text"
                id="pgatourId"
                name="pgatourId"
                value={formData.pgatourId}
                onChange={handleInputChange}
                placeholder="Enter PGA Tour ID"
              />
            </div>
          </div>

          <div className="edit-player-form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
              />
              <span className="checkbox-text">Active Player</span>
            </label>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={handleClose}
              className="cancel-btn"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="save-btn"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPlayerModal;
